import { Alert, Button, Card, cn } from "@breenova/ui";
import { BellOff, BellRing, Download, Share, Smartphone, Trash2 } from "lucide-react";
import { useState } from "react";

import { DetailHeader } from "@/components/app-shell";
import { useInstallState } from "@/lib/pwa";
import {
  pushSupported,
  usePushDevices,
  usePushRegistration,
  usePushSettings,
  useRemovePushDevice,
  useSendTestPush,
} from "@/lib/push";
import { SettingsFrame } from "./settings-frame";

/**
 * Installing the app, and letting it reach the breeder when they are not looking at it.
 *
 * The two live on one screen because on iOS they are one thing: Safari exposes the Push
 * API only to a home-screen app, so "turn on notifications" on an iPhone in a browser tab
 * is not a permission problem, it is an install problem — and a screen that offers the
 * toggle without saying that leaves the breeder tapping something that cannot work.
 */
export function NotificationsPage() {
  const install = useInstallState();
  const settings = usePushSettings();
  const registration = usePushRegistration(settings.data?.publicKey);
  const devices = usePushDevices();
  const removeDevice = useRemovePushDevice();
  const test = useSendTestPush();

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const supported = pushSupported();

  /*
   * The iPhone case, stated exactly. Safari has the Push API only for an installed app,
   * so on iOS in a tab the answer is not "your browser cannot" — it is "add it to your
   * home screen first", which is a completely different instruction.
   */
  const needsInstallFirst = install.ios && !install.standalone;

  async function run(action: () => Promise<unknown>, done?: string): Promise<void> {
    setMessage(null);
    setError(null);

    try {
      await action();

      if (done) {
        setMessage(done);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That did not work.");
    }
  }

  return (
    <div className="flex flex-col gap-md md:gap-lg">
      <DetailHeader backTo="/more" kicker="The app on your phone" title="Notifications" />

      <SettingsFrame>
        <section className="flex w-full flex-col gap-md">
          {error ? <Alert tone="error">{error}</Alert> : null}
          {message ? <Alert tone="success">{message}</Alert> : null}

          {/* ---- Install ---------------------------------------------------- */}
          {install.standalone ? (
            <Card tone="recessed" flat className="flex items-start gap-md p-lg">
              <span
                aria-hidden
                className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed-variant"
              >
                <Smartphone className="size-5" />
              </span>

              <div className="flex flex-col gap-hair">
                <p className="text-label text-on-surface">Installed</p>
                <p className="text-detail text-on-surface-variant">
                  BreeNova is running as an app on this device, with its own icon and its own
                  screen.
                </p>
              </div>
            </Card>
          ) : (
            <Card className="flex flex-col gap-md p-lg">
              <h2 className="text-card-title text-on-surface">Add BreeNova to your home screen</h2>

              {install.ios ? (
                <>
                  <p className="text-body text-on-surface-variant">
                    It opens full screen with its own icon, keeps you signed in, and it is the
                    only way an iPhone will let it send you notifications.
                  </p>

                  {/*
                   * Written out rather than offered as a button. Safari has never fired
                   * `beforeinstallprompt` and installs only through the Share sheet, which a
                   * page cannot open — so a button here would be one that does nothing on
                   * the platform this screen matters most on.
                   */}
                  <ol className="flex flex-col gap-sm">
                    <InstallStep
                      number={1}
                      icon={<Share className="size-4" aria-hidden />}
                      text="Tap the Share button in Safari — the square with an arrow coming out of it."
                    />
                    <InstallStep number={2} text="Scroll down and choose “Add to Home Screen”." />
                    <InstallStep
                      number={3}
                      text="Tap Add. BreeNova appears on your home screen like any other app."
                    />
                  </ol>

                  <p className="text-caption text-on-surface-variant">
                    It has to be Safari. Chrome and Firefox on iPhone cannot add an app to the
                    home screen — that restriction is Apple&rsquo;s, not BreeNova&rsquo;s.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-body text-on-surface-variant">
                    It opens in its own window with its own icon, and works offline enough to
                    show you where you were.
                  </p>

                  {install.canPrompt ? (
                    <Button className="self-start" onClick={() => void run(install.install)}>
                      <Download className="size-4" aria-hidden />
                      Install
                    </Button>
                  ) : (
                    <p className="text-caption text-on-surface-variant">
                      Your browser offers this from its own menu — look for “Install” or “Add
                      to home screen” in the address bar or the ⋮ menu.
                    </p>
                  )}
                </>
              )}
            </Card>
          )}

          {/* ---- Notifications ---------------------------------------------- */}
          <Card className="flex flex-col gap-md p-lg">
            <div className="flex flex-col gap-hair">
              <h2 className="text-card-title text-on-surface">Hatches and ringing days</h2>
              <p className="text-body text-on-surface-variant">
                One notification a morning, and only when something is due or already late.
                These are the two dates that cannot be caught up: a hatch you were not there
                for, and a ring that no longer fits.
              </p>
            </div>

            {settings.isPending ? (
              <p className="text-caption text-on-surface-variant">Checking…</p>
            ) : settings.data?.enabled !== true ? (
              <Alert tone="info">
                This server has not been set up to send notifications yet. Nothing on this
                device needs changing — the keys are configured where BreeNova is hosted.
              </Alert>
            ) : needsInstallFirst ? (
              <Alert tone="info">
                Add BreeNova to your home screen first, then open it from there. An iPhone only
                lets an installed app send notifications.
              </Alert>
            ) : !supported ? (
              <Alert tone="info">This browser cannot show notifications.</Alert>
            ) : registration.permission === "denied" ? (
              <Alert tone="warning">
                Notifications are blocked for BreeNova in this browser&rsquo;s settings. A page
                cannot ask again once they have been refused — you would have to allow them
                from the browser&rsquo;s own site settings.
              </Alert>
            ) : registration.subscribed ? (
              <div className="flex flex-wrap gap-sm">
                <Button
                  variant="secondary"
                  disabled={test.isPending}
                  onClick={() =>
                    void run(async () => {
                      const result = await test.mutateAsync();

                      if (result.sent === 0) {
                        throw new Error(
                          "No device accepted it. Try turning notifications off and on again.",
                        );
                      }
                    }, "Sent. It should arrive within a few seconds.")
                  }
                >
                  <BellRing className="size-4" aria-hidden />
                  {test.isPending ? "Sending…" : "Send a test"}
                </Button>

                <Button
                  variant="ghost"
                  className="text-error"
                  disabled={registration.busy}
                  onClick={() =>
                    void run(registration.disable, "This device will not be notified again.")
                  }
                >
                  <BellOff className="size-4" aria-hidden />
                  Turn off on this device
                </Button>
              </div>
            ) : (
              <Button
                className="self-start"
                disabled={registration.busy}
                onClick={() => void run(registration.enable)}
              >
                <BellRing className="size-4" aria-hidden />
                {registration.busy ? "Just a moment…" : "Turn on for this device"}
              </Button>
            )}
          </Card>

          {/* ---- Devices ----------------------------------------------------- */}
          {(devices.data?.length ?? 0) > 0 ? (
            <Card className="flex flex-col">
              <h2 className="px-lg pb-sm pt-lg text-label-caps uppercase text-on-surface-variant">
                Devices being notified
              </h2>

              <ul className="flex flex-col">
                {(devices.data ?? []).map((device) => {
                  const isThisOne = device.endpoint === registration.endpoint;

                  return (
                    <li
                      key={device.id}
                      className="flex min-h-16 items-center gap-md border-t border-outline-variant p-lg"
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-hair">
                        <span className="flex items-center gap-sm">
                          <span className="truncate text-label text-on-surface">
                            {device.deviceLabel ?? "A browser"}
                          </span>
                          {isThisOne ? (
                            <span className="shrink-0 text-tile-label uppercase text-primary">
                              This device
                            </span>
                          ) : null}
                        </span>

                        <span className="truncate text-caption text-on-surface-variant">
                          {device.lastNotifiedAt
                            ? `Last notified ${new Date(device.lastNotifiedAt).toLocaleDateString()}`
                            : "Not notified yet"}
                        </span>
                      </div>

                      <button
                        type="button"
                        aria-label={`Stop notifying ${device.deviceLabel ?? "this browser"}`}
                        disabled={removeDevice.isPending}
                        onClick={() =>
                          void run(async () => {
                            // Through the same path either way, so turning this device off
                            // from its own row also unsubscribes the browser rather than
                            // leaving it silently registered with a row that is gone.
                            if (isThisOne) {
                              await registration.disable();
                            } else {
                              await removeDevice.mutateAsync(device.id);
                            }
                          })
                        }
                        className={cn(
                          "flex size-touch shrink-0 items-center justify-center rounded-full",
                          "text-on-surface-variant transition-colors hover:bg-surface-container",
                        )}
                      >
                        <Trash2 className="size-4.5" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : null}

          <p className="text-caption text-on-surface-variant">
            Notifications are per browser, not per account. Signing in on a second device and
            turning them on there adds it to the list above; it does not move them.
          </p>
        </section>
      </SettingsFrame>
    </div>
  );
}

function InstallStep({
  number,
  icon,
  text,
}: {
  number: number;
  icon?: React.ReactNode;
  text: string;
}) {
  return (
    <li className="flex items-start gap-md">
      <span
        aria-hidden
        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-fixed text-tile-label text-on-primary-fixed-variant"
      >
        {number}
      </span>

      <span className="flex flex-1 items-center gap-sm text-body text-on-surface-variant">
        {icon}
        {text}
      </span>
    </li>
  );
}
